import { Loader2, Bell } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api';
import type { AttestationRecord } from '@/types/api';
import { cn } from '@/lib/utils';

interface AttestationRowProps {
  readonly attestation: AttestationRecord;
}

const STATUS_BADGE: Record<
  AttestationRecord['status'],
  { label: string; className: string }
> = {
  pending: { label: 'Pending', className: 'bg-amber-500/15 text-amber-400' },
  acknowledged: { label: 'Acknowledged', className: 'bg-green-500/15 text-green-400' },
  overdue: { label: 'Overdue', className: 'bg-red-500/15 text-red-400' },
};

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHr / 24);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function daysUntil(dateStr: string): number {
  const now = new Date();
  const due = new Date(dateStr);
  return Math.ceil((due.getTime() - now.getTime()) / 86_400_000);
}

function formatDueDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function AttestationRow({ attestation }: AttestationRowProps) {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();
  const badge = STATUS_BADGE[attestation.status];
  const days = daysUntil(attestation.dueDate);
  const isOwnAttestation = user?.userId === attestation.recipientId;
  const canAct =
    attestation.status === 'pending' || attestation.status === 'overdue';

  const acknowledgeMutation = useMutation({
    mutationFn: () => api.attestations.acknowledge(attestation.attestationId),
    onSuccess: () => {
      toast.success('Policy acknowledged.');
      queryClient.invalidateQueries({ queryKey: ['attestations'] });
    },
    onError: () => {
      toast.error('Failed to acknowledge. Please try again.');
    },
  });

  return (
    <div
      className={cn(
        'flex items-center gap-4 border-b border-border px-4 py-3.5 transition-colors',
        attestation.status === 'overdue' && 'bg-compliance-critical/5',
      )}
    >
      {/* Col 1: Employee */}
      <div className="min-w-0 w-48 shrink-0">
        <p className="truncate text-sm font-medium text-foreground">
          {attestation.recipientName}
        </p>
        <p className="truncate font-mono text-xs text-muted-foreground">
          {attestation.recipientId.slice(0, 8)}
        </p>
      </div>

      {/* Col 2: Document + Department */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground">
          {attestation.documentTitle}
        </p>
        <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
          {attestation.department.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
        </span>
      </div>

      {/* Col 3: Due date */}
      <div className="w-36 shrink-0 text-right">
        {attestation.status === 'overdue' ? (
          <p className="text-sm font-medium text-red-400">
            {formatDueDate(attestation.dueDate)}
            <span className="ml-1 text-xs">({Math.abs(days)}d overdue)</span>
          </p>
        ) : attestation.status === 'pending' ? (
          <p className="text-sm text-amber-400">
            {formatDueDate(attestation.dueDate)}
            <span className="ml-1 text-xs text-muted-foreground">
              (due in {days}d)
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {formatDueDate(attestation.dueDate)}
          </p>
        )}
      </div>

      {/* Col 4: Status */}
      <div className="w-32 shrink-0">
        <span
          className={cn(
            'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold',
            badge.className,
          )}
        >
          {badge.label}
        </span>
        {attestation.status === 'acknowledged' && attestation.acknowledgedAt && (
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatRelativeTime(attestation.acknowledgedAt)}
          </p>
        )}
      </div>

      {/* Col 5: Actions */}
      <div className="w-28 shrink-0 text-right">
        {canAct && isOwnAttestation && (
          <Button
            size="sm"
            className="bg-teal text-white hover:bg-teal/90"
            onClick={() => acknowledgeMutation.mutate()}
            disabled={acknowledgeMutation.isPending}
          >
            {acknowledgeMutation.isPending ? (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            ) : null}
            Acknowledge
          </Button>
        )}
        {canAct && !isOwnAttestation && (
          <Button variant="outline" size="sm">
            <Bell className="mr-1.5 h-3 w-3" />
            Remind
          </Button>
        )}
      </div>
    </div>
  );
}

export function AttestationRowSkeleton() {
  return (
    <div className="flex items-center gap-4 border-b border-border px-4 py-3.5">
      <div className="w-48 space-y-2">
        <div className="h-4 w-28 animate-pulse rounded bg-muted" />
        <div className="h-3 w-16 animate-pulse rounded bg-muted" />
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-4 w-48 animate-pulse rounded bg-muted" />
        <div className="h-4 w-16 animate-pulse rounded bg-muted" />
      </div>
      <div className="w-36">
        <div className="ml-auto h-4 w-24 animate-pulse rounded bg-muted" />
      </div>
      <div className="w-32">
        <div className="h-5 w-20 animate-pulse rounded bg-muted" />
      </div>
      <div className="w-28">
        <div className="ml-auto h-7 w-20 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}
